import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { PageHeader, ActionButton, SelectOption, PageContainer, ExportButton, showToast } from '../../components/shared';
import { PlusIcon } from '../../components/icons';
import { useExport } from '../../hooks';
import {
  useProducts, useProductsStats, useCategories, useSuppliers, useStores,
  useProduct, useCreateProduct, useUpdateProduct, useDeleteProduct,
} from '../../hooks';
import {
  ProductStatsRow, ProductFilterBar, ProductTable,
  type ProductStats, type ProductFilters, type ProductItem,
} from './components';
import { ProductFormModal } from './ProductFormModal';
import { ProductDeleteModal } from './ProductDeleteModal';
import { ProductTranslationsModal } from './ProductTranslationsModal';
import { BulkCreateProductsModal } from './BulkCreateProductsModal';
import { BulkUpdateCategoryModal } from './BulkUpdateCategoryModal';
import { ImportProductsModal } from './ImportProductsModal';
import {
  emptyProductForm, formFieldOrder, booleanFormFields, restrictionTriggerFields,
  type ProductListItem, type ProductModalMode, type ProductFormData,
  type ProductFormErrors, type ProductFormTouched, type StorePriceEntry,
} from './types';
import {
  extractList, extractCount, toPriceNumber, getCategoryName, getSupplierName,
  toProductFormData, validateProductField, validateProductForm,
  toCreatePayload, toUpdatePayload, mapProductToItem,
} from './utils';

import { useStoreSelectionStore } from '../../stores';

const initialFilters: ProductFilters = {
  search: '', categoryId: null, priceMin: '', priceMax: '', status: null,
};

/**
 * Tên field trong lỗi 400 của backend -> field trong form.
 *
 * Chỉ liệt kê những field mà việc hiện lỗi ngay tại ô có ích. Field không có ở
 * đây vẫn rơi về toast như cũ.
 */
const apiFieldToFormField: Record<string, keyof ProductFormData> = {
  alcohol_by_volume: 'alcoholByVolume',
  age_restriction_type: 'ageRestrictionType',
  min_age: 'minAge',
  sku: 'sku',
  name: 'name',
  unit: 'unit',
  price: 'basePrice',
};

// Parse Django ErrorDetail format into readable lines
function parseErrorMessage(rawMsg: string): string {
  const lines: string[] = [];
  const fieldRegex = /'([^']+)':\s*\[([^\]]+)\]/g;
  for (const fieldMatch of rawMsg.matchAll(fieldRegex)) {
    const field = fieldMatch[1];
    const msgRegex = /ErrorDetail\(string='([^']+)'/g;
    for (const msgMatch of fieldMatch[2].matchAll(msgRegex)) {
      lines.push(`${field}: ${msgMatch[1]}`);
    }
  }
  if (lines.length > 0) return lines.join('\n');
  const detailRegex = /ErrorDetail\(string='([^']+)'/g;
  for (const m of rawMsg.matchAll(detailRegex)) lines.push(m[1]);
  return lines.length > 0 ? lines.join('\n') : rawMsg;
}

export function ProductListPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const { selectedStoreId } = useStoreSelectionStore();
  const [filters, setFilters] = useState<ProductFilters>(() => ({
    ...initialFilters,
    search: searchParams.get('search') || '',
  }));
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ProductModalMode>(null);
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(emptyProductForm);
  /**
   * Ảnh chụp form lúc mới mở modal edit. `null` ở chế độ tạo mới.
   *
   * Dùng để phân biệt "người dùng đổi thông tin 18+" với "người dùng chỉ sửa
   * giá" — backend chỉ bật validate nồng độ cồn khi request động vào nhóm field
   * đó, và ta không muốn tự bật nó lên ở mọi lần lưu.
   */
  const [initialForm, setInitialForm] = useState<ProductFormData | null>(null);
  const [formErrors, setFormErrors] = useState<ProductFormErrors>({});

  useEffect(() => {
    const searchVal = searchParams.get('search') || '';
    setFilters((prev) => ({ ...prev, search: searchVal }));
    setCurrentPage(1);
  }, [searchParams]);

  const [formTouched, setFormTouched] = useState<ProductFormTouched>({});
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  /** Sản phẩm đang mở màn nhập bản dịch (mục #15). */
  const [translationsTarget, setTranslationsTarget] = useState<{ id: string; name: string } | null>(null);
  /** Mở modal tạo sản phẩm hàng loạt (mục #13/#21). */
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  /**
   * Sản phẩm đang chọn để cập nhật hàng loạt.
   *
   * Giữ `Set<string>` chứ không phải mảng: thao tác chính là tra "dòng này đã
   * chọn chưa" cho MỖI dòng ở MỖI lần render — với mảng thì đó là quét tuyến
   * tính nhân với số dòng.
   *
   * Lựa chọn SỐNG QUA cả đổi trang LẪN đổi bộ lọc — gom sản phẩm rải ở nhiều
   * trang, nhiều lần lọc rồi đổi danh mục một lượt là ca dùng thật. Lý do bỏ
   * cơ chế tự xoá khi đổi bộ lọc: xem khối chú thích ở `offPageCount`.
   */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [showBulkCategory, setShowBulkCategory] = useState(false);
  /** Mở modal import file CSV/XLSX (mục #13/#21) — khác bulk ở chỗ UPSERT theo sku. */
  const [showImport, setShowImport] = useState(false);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const pageSize = 50;

  // ── API filters ──────────────────────────────────────────────────────────────
  const apiFilters = useMemo(() => {
    const p: Record<string, string | number | undefined> = { page: currentPage, page_size: pageSize };
    if (selectedStoreId) p.store_id = selectedStoreId;
    if (filters.search.trim()) p.search = filters.search.trim();
    if (filters.categoryId) p.category_id = filters.categoryId;
    if ((filters as { supplierId?: string | null }).supplierId) p.supplier = (filters as { supplierId?: string | null }).supplierId!;
    if (filters.priceMin) p.price_min = Number.parseFloat(filters.priceMin);
    if (filters.priceMax) p.price_max = Number.parseFloat(filters.priceMax);
    if (filters.status) p.status = filters.status;
    return p;
  }, [filters, currentPage, pageSize, selectedStoreId]);

  // ── queries ──────────────────────────────────────────────────────────────────
  const { data: productsData, isLoading: isLoadingProducts, error: productsError } = useProducts(apiFilters);
  const { data: statsData } = useProductsStats();
  // Fetch all categories across all pages (API may cap limit)
  const { data: categoriesPage1 } = useCategories({ page: 1, limit: 200, isActive: true });
  const { data: categoriesPage2 } = useCategories({ page: 2, limit: 200, isActive: true });
  const { data: categoriesPage3 } = useCategories({ page: 3, limit: 200, isActive: true });
  const { data: suppliersData } = useSuppliers({ page_size: 200 });
  const { data: storesData } = useStores({ page_size: 200 });
  const { data: productDetailData, isLoading: isLoadingProductDetail } = useProduct(activeProductId ?? '', {
    enabled: !!activeProductId && (modalMode === 'view' || modalMode === 'edit'),
  });
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  // ── derived data ─────────────────────────────────────────────────────────────
  const productList = useMemo(() => extractList<ProductListItem>(productsData), [productsData]);
  const productsCount = useMemo(() => extractCount<ProductListItem>(productsData, productList.length), [productsData, productList.length]);

  // Server handles filtering and pagination — map directly to display items
  const products = useMemo(() => productList.map(mapProductToItem), [productList]);

  const stats: ProductStats = useMemo(() => ({
    totalProducts: statsData?.total ?? productsCount,
    activeProducts: statsData?.active ?? 0,
    inactiveProducts: statsData?.inactive ?? 0,
    lowStockAlerts: statsData?.low_stock ?? 0,
    totalValue: statsData?.total_value ?? 0,
    averagePrice: 0,
    withBarcodeRate: 0,
  }), [statsData, productsCount]);

  const isLoading = isLoadingProducts;
  const totalItems = productsCount;
  const totalPages = Math.ceil(totalItems / pageSize);
  const categories = useMemo(() => {
    const allPages = [categoriesPage1, categoriesPage2, categoriesPage3];
    const flat: { id: string; name: string; children?: unknown[] }[] = [];
    for (const page of allPages) {
      const items = extractList<{ id: string; name: string; children?: unknown[] }>(page);
      flat.push(...items);
    }
    // Deduplicate by id
    const seen = new Set<string>();
    const result: { id: string; name: string }[] = [];
    const walk = (items: typeof flat) => {
      for (const item of items) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          result.push({ id: item.id, name: item.name });
        }
        if (Array.isArray(item.children)) walk(item.children as typeof flat);
      }
    };
    walk(flat);
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesPage1, categoriesPage2, categoriesPage3]);
  const categoryOptions: SelectOption[] = useMemo(() => categories.map((c) => ({ value: c.id, label: c.name })), [categories]);
  const categoryMap = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);

  const suppliers = useMemo(() => extractList<{ id: string; name: string }>(suppliersData), [suppliersData]);
  const supplierOptions: SelectOption[] = useMemo(() => suppliers.map((s) => ({ value: s.id, label: s.name })), [suppliers]);
  const supplierMap = useMemo(() => new Map(suppliers.map((s) => [s.id, s.name])), [suppliers]);

  const stores = useMemo(() => extractList<{ id: string; name: string }>(storesData), [storesData]);
  const storeOptions: SelectOption[] = useMemo(() => stores.map((s) => ({ value: s.id, label: s.name })), [stores]);

  const activeProductFromList = useMemo(() => productList.find((p) => p.id === activeProductId) ?? null, [productList, activeProductId]);

  const productDetail = useMemo(() => {
    if (!productDetailData) return null;
    return productDetailData as ProductListItem;
  }, [productDetailData]);

  const activeCategoryName = useMemo(() => {
    if (import.meta.env.DEV) {
      console.log('[activeCategoryName] formData.categoryId:', formData.categoryId);
      console.log('[activeCategoryName] categoryMap has:', Array.from(categoryMap.keys()));
      console.log('[activeCategoryName] categoryMap.get:', categoryMap.get(formData.categoryId));
    }
    if (formData.categoryId && categoryMap.get(formData.categoryId)) return categoryMap.get(formData.categoryId)!;
    if (productDetail) return getCategoryName(productDetail);
    if (activeProductFromList) return getCategoryName(activeProductFromList);
    return 'Uncategorized';
  }, [formData.categoryId, categoryMap, productDetail, activeProductFromList]);

  const activeSupplierName = useMemo(() => {
    // First try: lookup from loaded suppliers map by UUID
    if (formData.supplierId && supplierMap.get(formData.supplierId)) return supplierMap.get(formData.supplierId)!;
    // Second: use supplier_name from product detail/list (API returns this directly)
    if (productDetail) return getSupplierName(productDetail);
    if (activeProductFromList) return getSupplierName(activeProductFromList);
    return '-';
  }, [formData.supplierId, supplierMap, productDetail, activeProductFromList]);

  const isSubmitting = createProduct.isPending || updateProduct.isPending;

  // ── modal helpers ─────────────────────────────────────────────────────────────
  const closeModal = useCallback(() => {
    setModalMode(null);
    setActiveProductId(null);
    setFormData(emptyProductForm);
    setInitialForm(null);
    setFormErrors({});
    setFormTouched({});
        if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (imageFileRef.current) imageFileRef.current.value = '';
  }, [imagePreview]);

  useEffect(() => {
    if (!modalMode) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [modalMode]);

  useEffect(() => {
    if (modalMode === 'create') {
      setFormData(emptyProductForm);
      setInitialForm(null);
      setImagePreview((prev) => prev !== null ? prev : null);
      return;
    }
    if ((modalMode === 'view' || modalMode === 'edit') && activeProductId) {
      const source = productDetail ?? activeProductFromList;
      if (source) {
        const data = toProductFormData(source);
        setFormData(data);
        // Bản gốc để so sánh: chỉ gửi nhóm field 18+ khi người dùng thật sự đổi
        // chúng, nếu không mọi lần sửa giá đều bật validate nồng độ cồn của BE.
        setInitialForm(data);
        // Only set imagePreview from data if user hasn't manually set one (blob = user picked file)
        setImagePreview((prev) => (prev !== null && prev.startsWith('blob:')) ? prev : (data.imageUrl || null));
      }
    }
  }, [modalMode, activeProductId, productDetail, activeProductFromList]);

  /**
   * Nút "Add product" (bug #236).
   *
   * Mở `BulkCreateProductsModal` chứ không phải `ProductFormModal`. Modal bulk
   * khởi tạo sẵn một dòng trống nên luồng tạo một sản phẩm không dài thêm bước
   * nào, còn người cần nhập cả lô thì không phải mở lại modal khác.
   *
   * `ProductFormModal` vẫn còn nguyên và vẫn được dùng cho mode 'view'/'edit'
   * khi bấm vào một dòng trong bảng — chỉ bỏ đường vào mode 'create'.
   */
  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Ô tích ở header — chỉ tác động lên các dòng của TRANG hiện tại. */
  const togglePage = useCallback((ids: string[], nextChecked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (nextChecked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  /** Số sản phẩm đã chọn KHÔNG nằm trong trang đang hiển thị. */
  const offPageCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    const onPage = products.reduce((n, row) => (selectedIds.has(row.id) ? n + 1 : n), 0);
    return selectedIds.size - onPage;
  }, [selectedIds, products]);

  /**
   * Lựa chọn SỐNG QUA cả đổi trang LẪN đổi bộ lọc.
   *
   * Bản đầu xoá sạch khi đổi bộ lọc, vì lo người dùng sửa nhầm một tập sản
   * phẩm họ không còn nhìn thấy. Nhưng nó chặn đúng cách dùng tự nhiên nhất:
   * lọc "sữa" chọn vài cái → lọc "sữa chua" chọn thêm → đổi danh mục một lượt.
   * Ép phải gom hết trong MỘT bộ lọc là buộc người dùng tự nghĩ ra một truy vấn
   * bao trọn mọi thứ họ cần, mà thường không có truy vấn đó.
   *
   * Rủi ro "sửa thứ không nhìn thấy" xử lý bằng cách NÓI RA, không phải bằng
   * cách xoá: thanh hành động ghi rõ bao nhiêu cái đang nằm ngoài trang đang
   * xem, và modal hiện lại tổng số trước khi bấm Lưu.
   */

  const handleAddProduct = useCallback(() => {
    setFormErrors({});
    setFormTouched({});
    setShowBulkCreate(true);
  }, []);

  const openProductModal = useCallback((id: string, mode: 'view' | 'edit' | 'delete-confirm') => {
    setActiveProductId(id); setModalMode(mode); setFormErrors({}); setFormTouched({});   }, []);

  const handleFieldChange = useCallback((field: keyof ProductFormData, value: string) => {
    setFormData((prev) => {
      // Checkbox đi qua đây dưới dạng chuỗi 'true'/'false'. Không đổi ngược lại
      // thì state giữ chuỗi 'false' — vẫn truthy — và ô tích không bao giờ bỏ được.
      const next = {
        ...prev,
        [field]: booleanFormFields.has(field) ? value === 'true' : value,
      };
      if (formTouched[field]) {
        setFormErrors((e) => ({ ...e, [field]: validateProductField(field, next, t, initialForm ?? undefined) }));
      }
      // re-validate compareAtPrice when basePrice changes and vice versa
      if (field === 'basePrice' && formTouched.compareAtPrice) {
        setFormErrors((e) => ({ ...e, compareAtPrice: validateProductField('compareAtPrice', next, t, initialForm ?? undefined) }));
      }
      if (field === 'compareAtPrice' && formTouched.basePrice) {
        setFormErrors((e) => ({ ...e, basePrice: validateProductField('basePrice', next, t, initialForm ?? undefined) }));
      }
      // Bốn field 18+ ràng buộc lẫn nhau: bật "hàng 18+", đổi phân loại hay bỏ
      // tích "là bia" đều có thể làm nồng độ cồn từ tuỳ chọn thành bắt buộc.
      // Không tính lại thì lỗi hiện sai một nhịp so với thứ đang thấy trên màn.
      if ((restrictionTriggerFields as readonly string[]).includes(field)) {
        setFormErrors((e) => ({
          ...e,
          ageRestrictionType: validateProductField('ageRestrictionType', next, t, initialForm ?? undefined),
          alcoholByVolume: validateProductField('alcoholByVolume', next, t, initialForm ?? undefined),
        }));
      }
      return next;
    });
  }, [formTouched, initialForm, t]);

  const handleStorePricesChange = useCallback((storePrices: StorePriceEntry[]) => {
    setFormData((prev) => ({ ...prev, storePrices }));
  }, []);

  const handleFieldBlur = useCallback((field: keyof ProductFormData) => {
    setFormTouched((prev) => ({ ...prev, [field]: true }));
    setFormErrors((prev) => ({ ...prev, [field]: validateProductField(field, formData, t, initialForm ?? undefined) }));
  }, [formData, initialForm, t]);

  const handleSubmitForm = useCallback(async () => {
    const errors = validateProductForm(formData, t, initialForm ?? undefined);
    setFormErrors(errors);
    setFormTouched(formFieldOrder.reduce<ProductFormTouched>((acc, k) => ({ ...acc, [k]: true }), {}));
    if (Object.keys(errors).length > 0) return;

    try {
      if (modalMode === 'create') {
        await createProduct.mutateAsync(toCreatePayload(formData));
        closeModal();
      } else if (modalMode === 'edit' && activeProductId) {
        await updateProduct.mutateAsync({
          id: activeProductId,
          data: toUpdatePayload(formData, initialForm ?? undefined),
        });
        closeModal();
      }
    } catch (err) {
      let msg = t('failedToSaveProduct');
      if (err && typeof err === 'object') {
        const e = err as {
          message?: string;
          details?: Array<{ field: string; message: string }>;
          originalError?: {
            response?: {
              data?: {
                message?: string;
                detail?: string;
                errors?: Record<string, string[] | string>;
              };
            };
          };
        };

        // 400 dạng `{errors: {alcohol_by_volume: [...]}}` — gắn thẳng vào field
        // tương ứng. Đổ một validation error của một field cụ thể vào toast là
        // bắt người dùng tự dò xem ô nào sai giữa hai chục ô.
        const apiErrors = e.originalError?.response?.data?.errors;
        if (apiErrors && typeof apiErrors === 'object') {
          const fieldErrors: ProductFormErrors = {};
          for (const [apiField, formField] of Object.entries(apiFieldToFormField)) {
            const raw = apiErrors[apiField];
            if (!raw) continue;
            fieldErrors[formField] = Array.isArray(raw) ? raw.join(' ') : String(raw);
          }
          if (Object.keys(fieldErrors).length > 0) {
            setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
            setFormTouched((prev) => ({
              ...prev,
              ...Object.fromEntries(Object.keys(fieldErrors).map((k) => [k, true])),
            }));
            showToast(Object.values(fieldErrors).join('\n'), 'error');
            return;
          }
        }

        if (e.details && Array.isArray(e.details) && e.details.length > 0) {
          msg = e.details.map((d) => `${d.field}: ${d.message}`).join('\n');
        } else if (e.message && !e.message.startsWith('Request failed')) {
          msg = parseErrorMessage(e.message);
        } else {
          const raw = e.originalError?.response?.data;
          if (raw?.message) msg = parseErrorMessage(raw.message);
          else if (raw?.detail) msg = parseErrorMessage(raw.detail);
          else if (e.message) msg = e.message;
        }
      }
      showToast(msg, 'error');
    }
  }, [activeProductId, closeModal, createProduct, formData, initialForm, modalMode, updateProduct, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!activeProductId) return;
    try {
      await deleteProduct.mutateAsync(activeProductId);
      closeModal();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('failedToDeleteProduct'), 'error');
    }
  }, [activeProductId, closeModal, deleteProduct, t]);

  const { triggerExport, isExporting } = useExport({ endpoint: '/products/', filePrefix: 'products' });

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <PageContainer>
      <PageHeader
        sectionLabel={t('productsSubtitle')}
        title={t('products')}
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            {/* Import file (mục #13/#21) — UPSERT theo sku, khác nút bên cạnh
                vốn chỉ TẠO MỚI từ dòng dán tay. */}
            <ActionButton variant="secondary" onClick={() => setShowImport(true)}>
              {t('importTitle')}
            </ActionButton>
            {/* MỘT nút tạo sản phẩm, không phải hai (bug #236).
                Trước đây "Add product" và "Bulk create products" đứng cạnh nhau
                nhưng làm cùng một việc: cả hai đều chỉ tạo được 1 sản phẩm mỗi
                lượt, vì form đơn lẻ vốn chỉ có một khối ô nhập. Hai nút trùng
                chức năng buộc người dùng phải đoán nút nào mới đúng.
                Giữ lại một nút "Add product" và cho nó mở thẳng modal tạo hàng
                loạt — modal đó mở ra với 1 dòng trống nên tạo một sản phẩm vẫn
                y như cũ, mà cần thêm thì bấm "thêm dòng". */}
            <ActionButton variant="primary" icon={<PlusIcon size={16} />} onClick={handleAddProduct}>
              {t('addProduct')}
            </ActionButton>
          </div>
        }
      />

      {productsError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">Không thể tải danh sách sản phẩm</p>
          <p className="mt-1 text-red-600">{productsError instanceof Error ? productsError.message : 'Đã xảy ra lỗi.'}</p>
        </div>
      )}

      <div className="mt-6"><ProductStatsRow stats={stats} isLoading={isLoading} /></div>

      <div className="mt-6">
        <ProductFilterBar
          filters={filters}
          onFiltersChange={(f) => { setFilters(f); setCurrentPage(1); }}
          onClearFilters={() => { setFilters(initialFilters); setCurrentPage(1); }}
          categories={categoryOptions}
          suppliers={supplierOptions}
          isLoading={isLoading}
        />
      </div>

      {/* Thanh hành động hàng loạt — chỉ hiện khi CÓ dòng được chọn.
          Đặt ngay trên bảng để "đã chọn bao nhiêu" và "các dòng được chọn" nằm
          cùng tầm mắt; treo ở chân trang thì phải cuộn mới biết mình đang thao
          tác trên mấy sản phẩm. */}
      {selectedIds.size > 0 && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
          <div>
            <span className="text-sm font-semibold text-primary-800">
              {t('bulkSelectedCount', { count: selectedIds.size })}
            </span>
            {/* Bao nhiêu cái đang nằm NGOÀI trang đang xem.
                Đây là cái giá của việc cho lựa chọn sống qua bộ lọc: người dùng
                nhìn bảng thấy 3 dòng được tích mà tổng ghi 37. Con số này giải
                thích chỗ 34 kia, thay vì để họ tưởng đếm sai.
                Chỉ so được với TRANG hiện tại — danh sách phân trang ở server
                nên FE không biết toàn bộ tập khớp bộ lọc. */}
            {offPageCount > 0 && (
              <span className="ml-2 text-xs text-primary-600">
                {t('bulkSelectedOffPage', { count: offPageCount })}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={clearSelection}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              {t('bulkClearSelection')}
            </button>
            <button
              onClick={() => setShowBulkCategory(true)}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-700"
            >
              {t('bulkCategoryAction')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6">
        <ProductTable
          data={products} isLoading={isLoading}
          currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setCurrentPage}
          onRowClick={(item) => openProductModal(item.id, 'view')}
          onEdit={(item) => openProductModal(item.id, 'edit')}
          onDelete={(item) => openProductModal(item.id, 'delete-confirm')}
          onTranslations={(item) => setTranslationsTarget({ id: item.id, name: item.name })}
          selectedIds={selectedIds}
          onToggleOne={toggleOne}
          onTogglePage={togglePage}
        />
      </div>

      {/* Bản dịch dữ liệu sản phẩm (mục #15) — modal riêng, không nhét vào
          form chính: form chính dùng chung cho cả tạo mới, mà sản phẩm chưa
          tồn tại thì chưa có id để gọi /products/{id}/translations/. */}
      {translationsTarget && (
        <ProductTranslationsModal
          productId={translationsTarget.id}
          productName={translationsTarget.name}
          onClose={() => setTranslationsTarget(null)}
        />
      )}

      {/* Import file CSV/XLSX (mục #13/#21) */}
      {showImport && <ImportProductsModal onClose={() => setShowImport(false)} />}

      {/* Cập nhật danh mục hàng loạt — ⚠️ chờ endpoint BE, xem modal. */}
      {showBulkCategory && (
        <BulkUpdateCategoryModal
          productIds={[...selectedIds]}
          categoryOptions={categoryOptions}
          onClose={() => setShowBulkCategory(false)}
          onDone={clearSelection}
        />
      )}

      {/* Tạo sản phẩm hàng loạt (mục #13/#21) */}
      {showBulkCreate && (
        <BulkCreateProductsModal
          categoryOptions={categoryOptions}
          supplierOptions={supplierOptions}
          storeOptions={storeOptions}
          onClose={() => setShowBulkCreate(false)}
        />
      )}

      {/* Delete modal */}
      <ProductDeleteModal
        isOpen={modalMode === 'delete-confirm'}
        isDeleting={deleteProduct.isPending}
        error={null}
        onClose={closeModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Form modal (view / create / edit) */}
      {(modalMode === 'view' || modalMode === 'create' || modalMode === 'edit') && (
        <ProductFormModal
          mode={modalMode}
          formData={formData}
          formErrors={formErrors}
          formTouched={formTouched}
          submitError={null}
          isSubmitting={isSubmitting}
          isLoadingDetail={isLoadingProductDetail}
          imagePreview={imagePreview}
          categoryOptions={categoryOptions}
          supplierOptions={supplierOptions}
          storeOptions={storeOptions}
          activeCategoryName={activeCategoryName}
          activeSupplierName={activeSupplierName}
          imageFileRef={imageFileRef}
          onClose={closeModal}
          onFieldChange={handleFieldChange}
          onFieldBlur={handleFieldBlur}
          onImagePreviewChange={setImagePreview}
          onStorePricesChange={handleStorePricesChange}
          onSubmit={handleSubmitForm}
        />
      )}
    </PageContainer>
  );
}
